import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { profileAction } from "@redux/modules";

// Components
import { PrivateRoute } from "@components";
import ScrollToTop from "@components/ScrollToTop";

// Public Pages
import LandingPage from "./public/LandingPage";
import Login from "./public/Auth/Login";
import PasswordReset from "./public/Auth/PasswordReset";
import SetNewPassword from "./public/Auth/SetNewPassword";
import SignUp from "./public/Auth/SignUp";
import TwoFactor from "./public/Auth/TwoFactor";
import PublicNotFound from "./public/NotFound";
import CookiePolicy from "./public/CookiePolicy";

// Private Pages
import AboutUs from "./private/AboutUs";
import Academy from "./private/Academy";
import AcademyDetail from "./private/AcademyDetail";
import BrandCart from "./private/Cart/BrandCart";
import BrandDetail from "./private/BrandDetail";
import BrandProductDetail from "./private/BrandProductDetail";
import Brands from "./private/Brands";
import Campaigns from "./private/Campaigns";
import CampaignDetail from "./private/CampaignDetail";
import Cart from "./private/Cart";
import Forum from "./private/Forum";
import GameFindMate from "./private/GameFindMate";
import GameGuessPicture from "./private/GameGuessPicture";
import GameQuestionWheel from "./private/GameQuestionWheel";
import GameScoreHunt from "./private/GameScoreHunt";
import GameWhoWantsScore from "./private/GameWhoWantsScore";
import GameWordPuzzles from "./private/GameWordPuzzles";
import BestPlate from "./private/BestPlate";
import Competitions from "./private/Competition";
import CompetitionPage from "./private/Competition/CompetitionPage";
import Home from "./private/Home";
import Leaderboard from "./private/Leaderboard";
import Magazine from "./private/Magazine";
import MagazineDetail from "./private/MagazineDetail";
import NotFound from "./private/NotFound";
import OldSubjects from "./private/Forum/OldSubjects";
import OrderSummary from "./private/OrderSummary";
import Profile from "./private/Profile";
import Search from "./private/Search";
import Shop from "./private/Shop";
import ShopDetail from "./private/ShopDetail";
import StaticPage from "./private/StaticPage";
import TermsCondition from "./private/Terms";
import Tv from "./private/Tv";
import Disclaimer from "./private/Disclaimer";

const Router = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const authUser = useSelector((store) => store.auth.authUser);
  const [tenantPaths, setTenantPaths] = useState([]);
  const [isPathChecking, setIsPathChecking] = useState(true); // Kontrol durumunu takip eder

  // Servisten yasaklı path'leri çekiyoruz
  useEffect(() => {
    if (isAuthenticated && authUser?.roleId) {
      setIsPathChecking(true); // Her auth durumunda veya rol değişiminde kontrolü başlat
      dispatch(profileAction.getTenantMenu(authUser.roleId))
        .then((res) => {
          if (res.isHttpSuccess && Array.isArray(res.data)) {
            const paths = res.data
              .map((item) => item.menu?.pathUrl)
              .filter(Boolean);
            setTenantPaths(paths);
          }
        })
        .finally(() => setIsPathChecking(false));
    } else {
      setIsPathChecking(false);
    }
  }, [isAuthenticated, authUser, dispatch]);

  // Mevcut path yasaklı mı kontrolü
  const isPathForbidden = tenantPaths.some((path) => {
    if (location.pathname === path) return true;
    if (path !== "/" && location.pathname.startsWith(path + "/")) return true;
    return false;
  });

  // 1. Veriler yüklenirken boş bir ekran veya global bir loading spinner göster
  // Bu sayede yasaklı sayfa içeriği saniyelik bile olsa asla render edilmez.
  if (isPathChecking && isAuthenticated) {
    return null; // veya <div className="loading">Yükleniyor...</div>
  }

  // 2. Eğer path yasaklıysa, Routes render edilmeden hemen yönlendir
  if (isAuthenticated && isPathForbidden) {
    return <Navigate to="/home" replace />;
  }

  // Authentication durumuna göre doğru NotFound bileşenini seç
  const NotFoundComponent = isAuthenticated ? NotFound : PublicNotFound;

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signUp" element={<SignUp />} />
        <Route path="/passwordReset" element={<PasswordReset />} />
        <Route path="/setNewPassword" element={<SetNewPassword />} />
        <Route path="/twoFactor" element={<TwoFactor />} />
        <Route path="/cerez-politikasi" element={<CookiePolicy />} />

        {/* Private Routes */}
        <Route
          path="/home"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Home />
            </PrivateRoute>
          }
        />

        {/* Sorumluluk Reddi Beyanı - login sonrası zorunlu onay sayfası.
            componentName YOK çünkü her tenantta her kullanıcı için zorunlu. */}
        <Route
          path="/sorumluluk-reddi-beyani"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Disclaimer />
            </PrivateRoute>
          }
        />

        <Route
          path="/brands"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="MARKALAR"
            >
              <Brands />
            </PrivateRoute>
          }
        />

        <Route
          path="/brands/:id"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="MARKALAR"
            >
              <BrandDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/brand-product-detail/:brandSubCategoryId/:brandStyleId"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="MARKALAR"
            >
              <BrandProductDetail />
            </PrivateRoute>
          }
        />

        {/* Kampanyalarımız - rol bazlı görünürlük menü deny-list (getTenantMenu)
            mekanizmasıyla yönetilir; componentName YOK. İçerik yalnızca USER
            (Satış Temsilcisi) rolüne backend tarafından döner. */}
        <Route
          path="/campaigns"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Campaigns />
            </PrivateRoute>
          }
        />

        <Route
          path="/campaigns/:id"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <CampaignDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/profile/:page"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Profile />
            </PrivateRoute>
          }
        />

        <Route
          path="/terms-and-conditions"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <TermsCondition />
            </PrivateRoute>
          }
        />

        <Route
          path="/shop"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="ÜRÜNLER"
            >
              <Shop />
            </PrivateRoute>
          }
        />

        <Route
          path="/shop/:id"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="ÜRÜNLER"
            >
              <ShopDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/search"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Search />
            </PrivateRoute>
          }
        />

        <Route
          path="/search/:id"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Search />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/find-mate"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="EŞLE KAZAN"
            >
              <GameFindMate />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/question-wheel"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="ŞANS ÇARKI"
            >
              <GameQuestionWheel />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/word-puzzles"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="KELİME AVI"
            >
              <GameWordPuzzles />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/guess-picture"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="RESMİ TAHMİN ET"
            >
              <GameGuessPicture />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/score-hunt"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="PUAN AVI"
            >
              <GameScoreHunt />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/who-wants-score"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="KİM 1000 PUAN İSTER"
            >
              <GameWhoWantsScore />
            </PrivateRoute>
          }
        />

        <Route
          path="/competitions"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="YARIŞMALAR"
            >
              <Competitions />
            </PrivateRoute>
          }
        />

        <Route
          path="/competitions/:id/:page?"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="YARIŞMALAR"
            >
              <CompetitionPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/games/best-plate"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="EN İYİ TABAK"
            >
              <BestPlate />
            </PrivateRoute>
          }
        />

        <Route
          path="/magazine"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="DERGİ"
            >
              <Magazine />
            </PrivateRoute>
          }
        />

        <Route
          path="/magazine/:id"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="DERGİ"
            >
              <MagazineDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/tv"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated} componentName="TV">
              <Tv />
            </PrivateRoute>
          }
        />

        <Route
          path="/forum"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="BİZİM MEYDAN"
            >
              <Forum />
            </PrivateRoute>
          }
        />

        <Route
          path="/forum/old-subjects"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="BİZİM MEYDAN"
            >
              <OldSubjects />
            </PrivateRoute>
          }
        />

        <Route
          path="/academy"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="AKADEMİ"
            >
              <Academy />
            </PrivateRoute>
          }
        />

        <Route
          path="/academy/:topicId"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="AKADEMİ"
            >
              <AcademyDetail />
            </PrivateRoute>
          }
        />

        <Route
          path="/leaderboard"
          element={
            <PrivateRoute
              isAuthenticated={isAuthenticated}
              componentName="LİDERLER TABLOSU"
            >
              <Leaderboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/cart"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Cart />
            </PrivateRoute>
          }
        />

        <Route
          path="/brand-cart"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <BrandCart />
            </PrivateRoute>
          }
        />

        <Route
          path="/static/:slug"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <StaticPage />
            </PrivateRoute>
          }
        />

        <Route
          path="/order-summary"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <OrderSummary />
            </PrivateRoute>
          }
        />

        <Route
          path="about-us"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <AboutUs />
            </PrivateRoute>
          }
        />

        {/* 404 Route - En sonda olmalı */}
        <Route path="/404" element={<NotFoundComponent />} />
        <Route path="*" element={<NotFoundComponent />} />
      </Routes>
    </>
  );
};

export default Router;
